<template>
  <div
    :class="{
      'obn-ObjectiveName-filteredout': filteredOut,
      'obn-ObjectiveName-clickable': nameAsLink
    }"
    class="obn-ObjectiveName"
    @click.self="handleClick"
  >
    <div class="o-title-text-wrapper">
      <div class="o-title-text" @click="onTitleClick">
        <template v-if="userHasReadAccess">
          <span
            v-if="withTooltip"
            v-tippy="{
              content: objective.name,
              placement: 'bottom-start',
              theme: DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES
            }"
          >
            {{ objective.name }}
          </span>
          <template v-else>
            {{ objective.name }}
          </template>
        </template>
        <template v-else> ∗∗∗∗∗∗∗∗∗∗∗ </template>

        <div
          v-if="!userHasReadAccess"
          :title="$t('objectives.unavailable_description')"
          class="o-title-text-overlay"
        />
      </div>
    </div>

    <PlatformElementViewer
      v-if="isJiraIssueOrWorkItem"
      :platform-element-id="objective.jiraIssueId"
    />

    <slot />
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { DoubleClickHandler } from '@/utils/double-click-handler'
import { isJiraIssueOrWorkItem } from '@/utils/objective-types'
import { currentUserCanReadObjective } from '@/utils/objectives'
import { isAnyTextSelected } from '@/utils/text-selection'

import PlatformElementViewer from '@/components/objectives/PlatformElementViewer'

const doubleClickHandler = new DoubleClickHandler()

export default defineComponent({
  name: 'ObjectiveName',

  components: {
    PlatformElementViewer
  },

  props: {
    objective: {
      type: Object,
      required: true
    },

    withTooltip: {
      type: Boolean
    },

    filteredOut: {
      type: Boolean
    },

    nameAsLink: {
      type: Boolean
    }
  },

  emits: { click: null },

  computed: {
    DROP_LIST_THEMES: () => DROP_LIST_THEMES,

    userHasReadAccess() {
      return currentUserCanReadObjective(this.objective)
    },

    isJiraIssueOrWorkItem() {
      return isJiraIssueOrWorkItem(this.objective)
    }
  },

  beforeUnmount() {
    doubleClickHandler.destroy()
  },

  methods: {
    onTitleClick() {
      doubleClickHandler.dispatch(this.handleClick)
    },

    handleClick() {
      if (!isAnyTextSelected()) {
        this.$emit('click')
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.obn-ObjectiveName {
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-1;
  min-height: 20px;
  display: flex;
  align-items: center;
  overflow: hidden;
  flex: 1 1 auto;
  gap: 4px;

  &-clickable {
    @media (any-hover: hover) {
      &:hover {
        text-decoration: underline;
        color: $primary-color-next;
        cursor: pointer;
      }
    }
  }
}

.o-title-text-wrapper {
  display: flex;
  align-items: center;
  overflow: hidden;

  .obn-ObjectiveName-filteredout & {
    opacity: 0.5;
  }
}

.o-title-text {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  user-select: text;
  line-height: 20px;

  .sv-Page-resize-continues & {
    user-select: none;
  }

  &:after {
    // Fix for Safari native tooltip. For prevent showing when content is truncated.
    content: '';
    display: block;
  }

  --gradient-color: 255, 255, 255;

  &-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    /* Permalink - use to edit and share this gradient: https://colorzilla.com/gradient-editor/#ffffff+0,ffffff+100&0+0,1+100 */
    background: -moz-linear-gradient(
      left,
      rgba(var(--gradient-color), 0) 0%,
      rgba(var(--gradient-color), 1) 100%
    ); /* FF3.6-15 */
    background: -webkit-linear-gradient(
      left,
      rgba($white, 0) 0%,
      rgba(var(--gradient-color), 1) 100%
    ); /* Chrome10-25,Safari5.1-6 */
    background: linear-gradient(
      to right,
      rgba($white, 0) 0%,
      rgba(var(--gradient-color), 1) 100%
    ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */

    .o-objective-row:hover &,
    .tb-RowWrapper:hover &,
    .asi-AppSelectOptionsItem-default-next.asi-AppSelectOptionsItem-focused &,
    .asi-AppSelectOptionsItem-default-next:hover &,
    .ol-ObjectivesList-default-next .oeli-ItemContent:hover &,
    .ob-DistantRelative & {
      --gradient-color: 245, 246, 247;
    }

    .asi-AppSelectOptionsItem-default-next.asi-AppSelectOptionsItem-selected & {
      --gradient-color: 217, 229, 247;
    }

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
